import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI } from '@/services/api';
import { invalidate } from '@/utils/cache';
import { formatISK, parseISK } from '@/utils/isk';
import { useI18n } from '@/contexts/I18nContext';
import { FolderOpen, Loader2, Camera, Clock, CheckCircle } from 'lucide-react';

/**
 * Zero-projects empty state with inline project creation form.
 * Shows success screen after creation with guided next steps.
 */
const CreateFirstProject = ({ headline, subtext, initialStep = 'cta' }) => {
  const { t, lang } = useI18n();
  const is = lang === 'is';
  const [step, setStep] = useState(initialStep);
  const [name, setName] = useState('');
  const [clientName, setClientName] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name || !clientName) return;
    setSaving(true);
    setError(null);
    try {
      await projectsAPI.create({
        name,
        client_name: clientName,
        billing_type: 'hourly',
        hourly_rate: parseISK(hourlyRate),
      });
      invalidate('projects', 'dashboard', 'time-entries', 'receipts');
      setStep('success');
    } catch (err) {
      setError(err.response?.data?.detail || t('could_not_create_project'));
    } finally {
      setSaving(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4" data-testid="project-created-success">
        <CheckCircle className="w-14 h-14 text-accent mb-5" strokeWidth={1.5} />
        <h2 className="text-lg font-semibold mb-2">{t('project_created')}</h2>
        <p className="text-sm text-foreground-muted mb-8 text-center max-w-xs">
          {name} {t('project_ready_next')}
        </p>
        <Link
          to="/receipts/new"
          className="btn btn-primary btn-lg w-full max-w-xs"
          data-testid="onboarding-scan-receipt"
        >
          <Camera size={18} />
          {t('scan_first_receipt')}
        </Link>
        <Link
          to="/time"
          className="btn btn-secondary mt-3 w-full max-w-xs"
          data-testid="onboarding-log-time"
        >
          <Clock size={18} />
          {t('log_hours')}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4" data-testid="no-projects-state">
      {step === 'cta' ? (
        <div className="w-full max-w-md">
          {/* Hero card - "einfalt" keyword first thing the user reads */}
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 text-center" data-testid="onboarding-hero">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-card-hover mb-4">
              <FolderOpen className="w-7 h-7 text-foreground-muted" strokeWidth={1.8} />
            </div>
            <h2 className="text-xl sm:text-2xl font-semibold mb-2">{headline}</h2>
            <p className="text-sm text-foreground-muted mb-6">{subtext}</p>
            <button
              onClick={() => setStep('form')}
              className="btn btn-primary btn-lg w-full"
              data-testid="create-first-project-btn"
            >
              {t('create_project_btn')}
            </button>
          </div>
          {/* Three steps - what they get after creating */}
          <ul className="mt-6 space-y-2 text-sm text-foreground-muted" data-testid="onboarding-steps">
            <li className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-card-hover flex items-center justify-center text-xs font-medium">1</span>
              <span>{is ? 'Búðu til verk, nafn, viðskiptavin og tímagjald.' : 'Create a project: name, client and hourly rate.'}</span>
            </li>
            <li className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-card-hover flex items-center justify-center text-xs font-medium">2</span>
              <span>{is ? 'Skráðu tíma eða skannaðu kvittanir í gegnum símann.' : 'Log hours or scan receipts from your phone.'}</span>
            </li>
            <li className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-card-hover flex items-center justify-center text-xs font-medium">3</span>
              <span>{is ? 'Í lok mánaðar sérðu hvað á að rukka og sækir gögnin fyrir bókarann.' : 'At month-end you see what to invoice and export the data for your bookkeeper.'}</span>
            </li>
          </ul>
        </div>
      ) : (
        <form onSubmit={handleCreate} noValidate className="w-full max-w-sm space-y-4" data-testid="first-project-form">
          <h2 className="text-lg font-semibold">{t('new_project')}</h2>
          {error && (
            <p className="text-error text-sm" data-testid="first-project-error">{error}</p>
          )}
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('project_name')}
            autoFocus
            required
            data-testid="first-project-name"
          />
          <input
            type="text"
            value={clientName}
            onChange={e => setClientName(e.target.value)}
            placeholder={t('client_name')}
            required
            data-testid="first-project-client"
          />
          <input
            type="text"
            inputMode="numeric"
            value={hourlyRate}
            onChange={e => setHourlyRate(formatISK(e.target.value))}
            placeholder={t('hourly_rate_excl_vat')}
            className="font-mono"
            data-testid="first-project-rate"
          />
          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={saving || !name || !clientName}
              className="btn btn-primary flex-1"
              data-testid="first-project-submit"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : t('create_project_btn')}
            </button>
            <button
              type="button"
              onClick={() => setStep('cta')}
              className="btn btn-secondary"
            >
              {t('cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CreateFirstProject;
